// src/pages/Auth/index.js

import React, { useEffect, useMemo, useRef } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Card,
  Grid,
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Slide,
  AppBar,
  Toolbar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";

import SoftBox from "components/SoftBox";
import logo from "assets/images/Anser.png";
import cartoonImage from "assets/images/Login-amico.svg";
import { useAuth } from "context/AuthContext";
import { useSystemTheme } from "context/SystemThemeContext";

import { useAuthFlow } from "./hooks/useAuthFlow";
import { useInspection } from "./hooks/useInspection";
import { useSession } from "./hooks/useSession";
import EmailStep from "./components/EmailStep";
import OtpStep from "./components/OtpStep";
import PasswordStep from "./components/PasswordStep";
import SignupForm from "./components/SignupForm";
import RegistrationForm from "./components/RegistrationForm";
import ForgotPasswordStep from "./components/ForgotPasswordStep";
import ProfileCard from "./components/ProfileCard";
import InspectionDialog from "./components/InspectionDialog";

import { AUTH_STEPS, STORAGE_KEYS } from "./constants";
import { clearAuthStorage, getInstallSource, isSuperAdmin } from "./utils";
import { PersonPinCircleOutlined } from "@mui/icons-material";

const Auth = () => {
  const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";
  
  const navigate = useNavigate();
  const { user, isAuthenticated, getDomains } = useAuth();
  const { referralCode } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  
  // Refs to prevent infinite loops
  const inspectionProcessedRef = useRef(false);
  const redirectProcessedRef = useRef(false);
  
  const {
    currentStep,
    setCurrentStep,
    loadingState,
    email,
    setEmail,
    otp,
    setOtp,
    password,
    setPassword,
    rememberMe,
    setRememberMe,
    snackbar,
    showSnackbar,
    hideSnackbar,
    resetAuthFlow,
    handleEmailSubmit,
    handleOtpSubmit,
    handlePasswordLogin,
    handleRegistrationSubmit,
    handleSignupSubmit,
    handleGoogleSignIn,
    handleForgotPassword,
    userEmail,
    userName
  } = useAuthFlow();
  
  const {
    isInspectionMode,
    isSwitchingUser,
    inspectDialogOpen,
    inspectTargetEmail,
    confirmInspection,
    cancelInspection,
    setInspectDialogOpen,
    openInspectionDialog,
    handleInspectionFromUrl,
  } = useInspection();
  
  const { initializeSession } = useSession();
  
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  useEffect(() => {
    const referralCodeFromPath = referralCode;
    const referralCodeFromQuery = queryParams.get("referral_code");
    const finalReferralCode = referralCodeFromPath || referralCodeFromQuery;
    
    if (finalReferralCode) {
      localStorage.setItem(STORAGE_KEYS.REFERRAL_CODE, finalReferralCode);
    } else {
      localStorage.removeItem(STORAGE_KEYS.REFERRAL_CODE);
    }
    
    const source = queryParams.get("source");
    if (source === "hubspot_marketplace") {
      localStorage.setItem(STORAGE_KEYS.INSTALL_SOURCE, JSON.stringify({
        value: "hubspot_marketplace",
        timestamp: Date.now()
      }));
    }
    
    const secret = queryParams.get("secret");
    if (secret) {
      localStorage.setItem("secret", secret);
    }
    
    const domainId = queryParams.get("domain_id");
    if (domainId) {
      localStorage.setItem("domain_id", domainId);
    }
  }, [location.search, referralCode, queryParams]);
  
  useEffect(() => {
    if (!isAuthenticated() || !user) return;
    
    const inspectParam = queryParams.get("inspect");
    if (inspectParam || isInspectionMode) return;
    
    const secret = queryParams.get("secret");
    const domainId = queryParams.get("domain_id");
    const roleParam = queryParams.get("role");
    const idParam = queryParams.get("id");
    const hasAuthParams = secret || domainId || roleParam || idParam;
    
    if (hasAuthParams) return;
    
    if (currentStep === AUTH_STEPS.EMAIL && !redirectProcessedRef.current) {
      redirectProcessedRef.current = true;
      
      const isSuperAdminUser = isSuperAdmin(user?.is_super_admin);
      const domains = getDomains();
      const hasDomains = domains && domains.length > 0;
    
      if (isSuperAdminUser) {
        navigate("/customers");
      } else if (hasDomains && domains[0].role === "read only") {
        navigate("/dashboard");
      } else if (hasDomains) {
        navigate("/settings");
      }
    }
  }, [isAuthenticated, user, queryParams, navigate, currentStep, getDomains, isInspectionMode]);
  
useEffect(() => {
  const inspectEmail = queryParams.get("inspect");

  if (!inspectEmail) return;
  if (inspectionProcessedRef.current) return;

  const urlParams = new URLSearchParams(location.search);
  urlParams.delete("inspect");
  const newSearch = urlParams.toString();
  const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "");
  window.history.replaceState({}, "", newUrl);

  inspectionProcessedRef.current = true;

  const performDirectInspection = async () => {
    try {
      const success = await handleInspectionFromUrl(inspectEmail);
      if (!success) {
        showSnackbar("Failed to start inspection mode. Please login first.", "error");
        inspectionProcessedRef.current = false;
      }
    } catch (error) {
      console.error("Inspection error:", error);
      showSnackbar("Failed to start inspection mode", "error");
      inspectionProcessedRef.current = false;
    }
  };
  performDirectInspection();
}, [queryParams]);
  
  const getStepContent = () => {
    switch(currentStep) {
      case AUTH_STEPS.REGISTRATION:
        return (
          <RegistrationForm 
            email={email} 
            isDarkMode={isDarkMode} 
            loadingState={loadingState}
            onSubmit={handleRegistrationSubmit}
            showSnackbar={showSnackbar}
          />
        );
      case AUTH_STEPS.SIGNUP:
        return (
          <SignupForm 
            email={email} 
            isDarkMode={isDarkMode}
            loadingState={loadingState}
            onSubmit={handleSignupSubmit}
            showSnackbar={showSnackbar}
          />
        );
      case AUTH_STEPS.PASSWORD:
        return (
          <PasswordStep
            email={email}
            password={password}
            setPassword={setPassword}
            rememberMe={rememberMe}
            setRememberMe={setRememberMe}
            loadingState={loadingState}
            onSubmit={handlePasswordLogin}
            onForgotClick={() => setCurrentStep(AUTH_STEPS.FORGOT_PASSWORD)}
            onBack={resetAuthFlow}
            isDarkMode={isDarkMode}
          />
        );
      case AUTH_STEPS.OTP:
        return (
          <OtpStep
            email={email}
            otp={otp}
            setOtp={setOtp}
            loadingState={loadingState}
            onSubmit={handleOtpSubmit}
            onBack={resetAuthFlow}
            isDarkMode={isDarkMode}
          />
        );
      case AUTH_STEPS.FORGOT_PASSWORD:
        return (
          <ForgotPasswordStep 
            isDarkMode={isDarkMode}
            loadingState={loadingState}
            onSubmit={handleForgotPassword}
            onBack={() => setCurrentStep(AUTH_STEPS.PASSWORD)}
          />
        );
      case AUTH_STEPS.EMAIL:
      default:
        return (
          <EmailStep
            email={email}
            setEmail={setEmail}
            loadingState={loadingState}
            onSubmit={handleEmailSubmit}
            isDarkMode={isDarkMode}
          />
        );
    }
  };
  
  const getStepTitle = () => {
    switch(currentStep) {
      case AUTH_STEPS.REGISTRATION:
        return "Domain Registration";
      case AUTH_STEPS.SIGNUP:
        return "Complete Your Signup";
      case AUTH_STEPS.FORGOT_PASSWORD:
        return "Forgot Password";
      case AUTH_STEPS.PASSWORD:
        return "Welcome Back";
      case AUTH_STEPS.OTP:
        return "Verify Your Email";
      default:
        return "Login/Signup to Ansera AI";
    }
  };
  
  const getStepSubtitle = () => {
    switch(currentStep) {
      case AUTH_STEPS.REGISTRATION:
        return "Add your domain details to complete registration";
      case AUTH_STEPS.SIGNUP:
        return "Set up your password to complete registration";
      case AUTH_STEPS.FORGOT_PASSWORD:
        return "Enter your email and we'll send a reset link";
      case AUTH_STEPS.PASSWORD:
        return "Enter your password to continue";
      case AUTH_STEPS.OTP:
        return "Enter the verification code we sent to your email";
      default:
        return "To access your dashboard";
    }
  };
  
  const showSocialLogin = currentStep === AUTH_STEPS.EMAIL;
  
  return (
    <>
      <AppBar
        position="static"
        color="inherit"
        sx={{
          boxShadow: "none",
          backgroundColor: isDarkMode ? "#060C18" : "white",
          borderBottom: `1px solid ${isDarkMode ? "#2d2d3f" : "#e0e0e0"}`,
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", px: 2 }}>
          <Box display="flex" alignItems="center">
            <img 
              src={logo} 
              alt="Ansera Logo" 
              style={{ 
                height: "3rem",
                filter: isDarkMode ? "brightness(0) invert(1)" : "none",
              }} 
            />
          </Box>
          
          {(currentStep === AUTH_STEPS.REGISTRATION || currentStep === AUTH_STEPS.SIGNUP) && (
            <ProfileCard isDarkMode={isDarkMode} onLogout={resetAuthFlow} />
          )}
        </Toolbar>
      </AppBar>
      
        {isSwitchingUser && (
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              backgroundColor: isDarkMode ? "rgba(6,12,24,0.92)" : "rgba(255,255,255,0.92)",
              backdropFilter: "blur(6px)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 3,
              transition: "background-color 0.3s ease",
            }}
          >
            <Box sx={{ position: "relative", width: 72, height: 72 }}>
              <CircularProgress
                size={72}
                thickness={2}
                sx={{ color: "#219297", position: "absolute", top: 0, left: 0 }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PersonPinCircleOutlined sx={{ fontSize: 28, color: "#219297" }} />
              </Box>
            </Box>
            
            <Box textAlign="center">
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{ color: isDarkMode ? "#e2e8f0" : "#344767", mb: 0.5 }}
              >
                Logging in...
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: isDarkMode ? "#a0aec0" : "#67748e" }}
              >
                Please wait while we switch your session
              </Typography>
            </Box>
          </Box>
        )}
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={(props) => <Slide {...props} direction="down" />}
      >
        <Alert
          onClose={hideSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
            color: "#fff",
            backgroundColor:
              snackbar.severity === "success"
                ? "#219297"
                : snackbar.severity === "error"
                ? "#d32f2f"
                : snackbar.severity === "warning"
                ? "#ed6c02"
                : snackbar.severity === "info"
                ? "#0288d1"
                : undefined,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      <SoftBox
        sx={{
          minHeight: "calc(100dvh - 64px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgColor: isDarkMode ? "#060C18" : "#f0f4f7",
          px: 2,
          py: { xs: 2, sm: 2 },
          overflowY: "auto",
          transition: "background-color 0.3s ease",
        }}
      >
        <Grid container spacing={4} alignItems="center" justifyContent="center" maxWidth="xl">
          {!isMobile && showSocialLogin && (
            <Grid item md={6} lg={6} xl={6} sx={{ display: { xs: "none", md: "block" } }}>
              <Box
                component="img"
                src={cartoonImage}
                alt="Login Illustration"
                sx={{
                  width: "100%",
                  maxWidth: { md: "350px", lg: "450px", xl: "550px" },
                  mx: "auto",
                }}
              />
            </Grid>
          )}
          
          <Grid item xs={12} sm={10} md={6} lg={6} xl={6}>
            <Card
              sx={{
                p: { xs: 3, sm: 4 },
                maxWidth: 480,
                mx: "auto",
                boxShadow: isDarkMode ? "0 10px 30px rgba(0,0,0,0.3)" : "0 10px 30px rgba(0,0,0,0.05)",
                borderRadius: 4,
                backgroundColor: isDarkMode ? "#060C18" : "#fff",
                transition: "background-color 0.3s ease, box-shadow 0.3s ease",
              }}
            >
              <Grid container direction="column" spacing={3} alignItems="center">
                <Grid item>
                  <Typography
                    fontWeight="bold"
                    align="center"
                    sx={{
                      fontSize: "clamp(1.4rem, 2.5vw, 2rem)",
                      color: isDarkMode ? "#e2e8f0" : "text.primary",
                    }}
                  >
                    {getStepTitle()}
                  </Typography>
                  <Typography
                    variant="body2"
                    align="center"
                    sx={{
                      fontSize: "clamp(0.8rem, 1.2vw, 1rem)",
                      color: isDarkMode ? "#a0aec0" : "text.secondary",
                    }}
                  >
                    {getStepSubtitle()}
                  </Typography>
                </Grid>
                
                <Grid item sx={{ width: "100%" }}>
                  {getStepContent()}
                </Grid>
                
                {showSocialLogin && (
                  <>
                    <Grid item>
                      <Typography
                        variant="body2"
                        align="center"
                        sx={{ fontSize: "clamp(0.8rem, 1vw, 1rem)", color: isDarkMode ? "#a0aec0" : "text.secondary" }}
                      >
                        OR
                      </Typography>
                    </Grid>
                    <Grid item>
                      <GoogleLogin
                        theme="filled_blue"
                        text="continue_with"
                        onSuccess={handleGoogleSignIn}
                        onError={() => console.log("Google login failed")}
                      />
                      {loadingState === 'google' && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                          <CircularProgress size={24} sx={{ color: "#219297" }} />
                          <Typography variant="body2" sx={{ ml: 1, color: isDarkMode ? "#e2e8f0" : undefined }}>
                            Signing in with Google...
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  </>
                )}
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </>
  );
};

export default Auth;